import { useDispatch, useSelector } from "react-redux";
import React, { memo, useCallback, useEffect, useState } from "react";
import { getUserAccountsSelector, getUserSelectedAccountCode, } from "../../../../app/appSelectors.js";
import { setUserSelectedAccountCode } from "../../../../app/appActions.js";
import { ACCOUNT_TYPES } from "../../../../app/appConstants.js";
import _ from "lodash";
import { BinIcon } from "../../../../components/icon/Icon.jsx";
import parseAccountName from "../../../../utils/parseAccountName.js";
import {
    LiteTradeSelectAccountInfoDescriptionStyled,
    LiteTradeSelectAccountInfoStyled,
    LiteTradeSelectAccountListStyled,
    LiteTradeSelectAccountStyled,
    LiteTradeSelectAccountTitleStyled,
    SelectAccountToggleRowStyled,
    SelectAccountToggleStyled
} from "./styled/LiteTradeSelectAccountStyled.js";
import { Slider } from "../../../../components/Slider/Slider.jsx";
import { SelectToggleButton } from "../../../../components/SelectToggleButton/SelectToggleButton.jsx";
import amountFormat from "../../../../utils/amountFormat.js";
import { getLiteTradeAllAccountsMetrics } from "../../liteTradeSelectors.js";
import { useLiteTradeSelectAccountAppsflyerEvents } from "./liteTradeSelectAccountHooks";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext";
import LiteTradeSelectAccountRow from "./LiteTradeSelectAccountRow";

const SelectAccountToggle = memo(({onClick, showCurrentValue, type, value}) => {
    const {getLangPath} = useLangContext();
    return <SelectAccountToggleStyled type={type} onClick={onClick}>
        <SelectToggleButton>
            {
                type === ACCOUNT_TYPES.DEMO && <span className={'icon'}><BinIcon name={'IcEducation'}/></span>
            }
            <SelectAccountToggleRowStyled>
                <div>{getLangPath(`lite_version.home.${type === ACCOUNT_TYPES.DEMO ? 'account_demo' : 'account_real'}`)}</div>
                {!!showCurrentValue && <div className={'value'}>{value}</div>}
            </SelectAccountToggleRowStyled>
        </SelectToggleButton>
    </SelectAccountToggleStyled>
});

function LiteTradeSelectAccount(props) {
    const {
        metricParam = 'equity', // version,equity,balance,availableBalance,credit,marginFree,openPL,totalPL,margin,openPositionsCount,openOrdersCount,
        showCurrentValue,
    } = props;

    const dispatch = useDispatch();
    const accounts = useSelector(getUserAccountsSelector);
    const metrics = useSelector(getLiteTradeAllAccountsMetrics);
    const selectedAccountCode = useSelector(getUserSelectedAccountCode);

    const [parsedAccounts, setParsedAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const {
        handleSelectAccountCancelAppsflyer,
        handleSelectToggleClickAppsflyer,
        handleAccountSelectAppsflyer,
    } = useLiteTradeSelectAccountAppsflyerEvents();
    const {getLangPath} = useLangContext();

    useEffect(() => {
        setParsedAccounts(accounts.map(item => parseAccountName(item.name)));
    }, [accounts]);

    useEffect(() => {
        setSelectedAccount(_.find(parsedAccounts, {accountCode: selectedAccountCode}));
    }, [parsedAccounts, selectedAccountCode]);

    const handleAccountSelect = useCallback((e) => {
        const value = e.currentTarget.dataset.value;
        const account = _.find(parsedAccounts, {accountCode: value});
        setSelectedAccount(account);
        dispatch(setUserSelectedAccountCode(value));
        handleAccountSelectAppsflyer(value);
    }, [dispatch, handleAccountSelectAppsflyer, parsedAccounts]);

    const handleSelectAccountToggleClick = useCallback(() => {
        handleSelectToggleClickAppsflyer();
    }, [handleSelectToggleClickAppsflyer]);

    const handleSelectAccountCancel = useCallback(() => {
        handleSelectAccountCancelAppsflyer();
    }, [handleSelectAccountCancelAppsflyer]);

    if (!selectedAccount || !metrics?.[selectedAccount.accountCode]) {
        return null;
    }

    return <LiteTradeSelectAccountStyled>

        <Slider showCloseButton={false}
                onClose={handleSelectAccountCancel}
                toggleComponent={<SelectAccountToggle
                    onClick={handleSelectAccountToggleClick}
                    showCurrentValue={showCurrentValue}
                    type={selectedAccount.type}
                    value={amountFormat(metrics[selectedAccount.accountCode][metricParam], 2, '$')}
                />}
                placement={'bottom'}>
            <LiteTradeSelectAccountTitleStyled>{getLangPath('lite_version.home.accounts')}</LiteTradeSelectAccountTitleStyled>
            <LiteTradeSelectAccountListStyled>
                {accounts.map(account => <LiteTradeSelectAccountRow
                    account={account}
                    handleAccountSelect={handleAccountSelect}
                    key={account.name}
                    metricParam={metricParam}
                />)}
            </LiteTradeSelectAccountListStyled>
            <LiteTradeSelectAccountInfoStyled>
                <div className="d-flex align-items-center me-2">
                    <BinIcon name={'IcInfo'}/>
                </div>
                <LiteTradeSelectAccountInfoDescriptionStyled>
                    {getLangPath(`lite_version.home.switch_to_${_.toLower(selectedAccount.type) === ACCOUNT_TYPES.DEMO ? 'real' : 'demo'}`)}
                </LiteTradeSelectAccountInfoDescriptionStyled>
            </LiteTradeSelectAccountInfoStyled>
        </Slider>
    </LiteTradeSelectAccountStyled>
}

export default LiteTradeSelectAccount;
